import { useState } from 'react';
import { Modal, Stack, Group, Button, Alert, Select } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import dayjs from 'dayjs';
import { api } from '../../api/client';
import { useAuth, canWrite } from '../../store/auth';

export interface StatusLogEntry {
  id: number;
  equipmentId: number;
  status: string;
  eventDate: string;
  editedByUserId: number | null;
  editedByUsername: string | null;
  createdAt: string;
}

const STATUS_OPTIONS = ['Available', 'Broken', 'Dead'] as const;

export default function StatusLogModal({
  equipmentId,
  initial,
  onClose,
  onDone,
}: {
  equipmentId: number;
  initial: StatusLogEntry | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const { me } = useAuth();
  const writable = canWrite(me?.role);
  const [date, setDate] = useState<Date | null>(initial ? new Date(initial.eventDate) : new Date());
  const [status, setStatus] = useState<string>(initial?.status ?? 'Available');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    setErr(null);
    if (!date) return setErr('Date is required.');
    if (!STATUS_OPTIONS.includes(status as any)) return setErr('Invalid status.');
    setBusy(true);
    try {
      const body = { status, eventDate: dayjs(date).format('YYYY-MM-DD') };
      if (initial) await api.put(`/equipment/${equipmentId}/status-log/${initial.id}`, body);
      else await api.post(`/equipment/${equipmentId}/status-log`, body);
      onDone();
    } catch (e: any) {
      setErr(e.message ?? 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal opened onClose={onClose} title={<span className="modal-title-custom">{initial ? 'Edit Status Log' : 'Add Status Log'}</span>}>
      <Stack>
        <DateInput label="Date of event" value={date} onChange={setDate} disabled={!writable} />
        <Select
          label="Status"
          data={[...STATUS_OPTIONS]}
          value={status}
          onChange={(v) => v && setStatus(v)}
          disabled={!writable}
        />
        {err && <Alert color="red">{err}</Alert>}
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>Close</Button>
          {writable && <Button onClick={save} loading={busy}>Save</Button>}
        </Group>
      </Stack>
    </Modal>
  );
}
